import { emergencyNumber } from "./phone.ts";
import { isPlatformRefusal } from "./platform.ts";
import type {
  ActiveSession,
  Activity,
  ActivityChanged,
  ActivityList,
  ActivityTypeList,
  AddItems,
  ApiErrorBody,
  AssistantSession,
  AssistantTurn,
  BugReportAccepted,
  BugReportUpload,
  BulkItems,
  BulkResult,
  CallDetail,
  CallList,
  CallListList,
  CallListParams,
  CallMonitorEvent,
  CallParticipants,
  CallQualityUpload,
  CallSummaryRow,
  CallTarget,
  ChangeDisposition,
  ChatStreamEvent,
  ChoicePrompt,
  ClientEvent,
  CoachingNote,
  CoachingNoteList,
  ConnectOdooRequest,
  ContactBrowseParams,
  ContactContext,
  ContactList,
  ContactPage,
  ContactRow,
  ContactUpdate,
  ContextCard,
  ConversationSummary,
  CrmRef,
  CrmStatus,
  CrmSyncStarted,
  CrmSyncStatus,
  Destination,
  DeviceInfo,
  DevSignInRequest,
  DispositionChanged,
  DispositionList,
  DispositionsQuery,
  FocusSession,
  ItemOutcome,
  ItemPatch,
  ItemStatus,
  ItemsAdded,
  ListDetail,
  ListItem,
  ListPatch,
  ListSort,
  LiveCallList,
  MarkSeenRequest,
  MarkWonStatus,
  Me,
  MonitorMode,
  MyConnector,
  NewActivity,
  NewList,
  NewTurn,
  NextItem,
  NextRequest,
  NextStepActivities,
  NextStepActivitiesSettings,
  Page,
  Participant,
  PdialAvailabilityView,
  PdialSession,
  PdialSessionPatch,
  PdialStarted,
  PhotoList,
  PickupMode,
  PickupResponse,
  QueueItems,
  RecordingState,
  RecordingUrl,
  RefreshResult,
  ReportDraft,
  ReportProgress,
  ResolveRequest,
  ResolveResponse,
  RestoreResult,
  Session,
  SessionSummary,
  SignInRoute,
  SortResult,
  StartAssistant,
  StartCallRequest,
  StartCallResponse,
  SupportUnread,
  SyncResult,
  TagList,
  TeamList,
  TelephonyToken,
  Today,
  TodayBulk,
  TodayBulkResult,
  ToolInvocation,
  ToolResult,
  TransferRequest,
  UnseenCounts,
  Upcoming,
  UserSettings,
  VoiceGrant,
  Wrapup,
  WrapupActivity,
  WrapupDraft,
  WrapupDraftList,
} from "./types.ts";
import {
  appendFile,
  bodyReader,
  cancelBody,
  isAbortError,
  utf8Decoder,
  type WebRequestInit,
} from "./web.ts";
import type { WhatsNewList } from "./whatsNew.ts";

/** Where the long-lived refresh token lives. On desktop this is the OS keychain. */
export interface TokenStore {
  getRefreshToken(): Promise<string | null>;
  setRefreshToken(token: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryTokenStore implements TokenStore {
  private token: string | null;
  constructor(initial: string | null = null) {
    this.token = initial;
  }
  async getRefreshToken() {
    return this.token;
  }
  async setRefreshToken(token: string) {
    this.token = token;
  }
  async clear() {
    this.token = null;
  }
}

/** A failed API call. `status` 0 means the request never reached the server. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  /** The whole error body, for errors that carry more than the message (`423 ai_paused`). */
  readonly body: Record<string, unknown> | undefined;
  /** Seconds to wait before trying again, from `Retry-After`, when the server sent one. */
  retryAfter: number | undefined;
  constructor(
    status: number,
    code: string,
    message: string,
    requestId?: string,
    body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.body = body;
  }
  /** The server can't take this right now (`503 busy`); the same request works shortly. */
  get isBusy() {
    return this.status === 503 && this.code === "busy";
  }
  get isNetwork() {
    return this.status === 0;
  }
  get isAuth() {
    return this.status === 401;
  }
  /** The seat does not include this (`403 not_in_plan`, ADR 0033): never worth a toast. */
  get isNotInPlan() {
    return this.status === 403 && this.code === "not_in_plan";
  }
  /** The company is suspended (`403 paused`, ADR 0054). */
  get isPaused() {
    return this.code === "paused";
  }
  /** The company's phone service is still being set up (`409 setting_up`, ADR 0054). */
  get isSettingUp() {
    return this.code === "setting_up";
  }
  /** One of the platform's refusals (ADR 0054): show it, don't retry it quickly. */
  get isPlatformRefusal() {
    return isPlatformRefusal(this.code);
  }
}

/**
 * Every wrap-up and disposition change this client sends says whether to book an activity
 * (`activity`, null for none). The server reads absent as none too; sending it keeps the
 * request explicit.
 */
export function withActivity<T extends { activity?: WrapupActivity | null }>(
  body: T,
): T & { activity: WrapupActivity | null } {
  return { ...body, activity: body.activity ?? null };
}

/** What a path segment holds, for `EmptyIdError`: `/v1/calls//wrapup` is missing a call. */
const SEGMENT_NAMES: Record<string, string> = {
  calls: "call",
  lists: "list",
  items: "list item",
  sessions: "session",
  conversations: "conversation",
  messages: "message",
  contacts: "contact",
  team: "teammate",
  campaigns: "campaign",
  drafts: "draft",
};

/**
 * A request refused before it was sent: a path segment that should hold an id is empty
 * (`/v1/calls//wrapup`). It never reaches the server. `what` names the missing thing ("call").
 */
export class EmptyIdError extends ApiError {
  readonly what: string;
  constructor(what: string) {
    super(400, "empty_id", `No ${what} to send this to.`);
    this.name = "EmptyIdError";
    this.what = what;
  }
}

/** The missing thing when `path` has an empty segment, else null. Exported for tests. */
export function emptySegment(path: string): string | null {
  const bare = path.split(/[?#]/, 1)[0] ?? "";
  if (!bare.startsWith("/")) return null;
  const parts = bare.split("/");
  // "/v1/calls//wrapup" splits into ["", "v1", "calls", "", "wrapup"]; the leading "" is the root.
  for (let i = 1; i < parts.length; i++) {
    if (parts[i] !== "") continue;
    const before = parts[i - 1] ?? "";
    return SEGMENT_NAMES[before] ?? (before ? before.replace(/s$/, "") : "id");
  }
  return null;
}

export type AuthState = "signed_in" | "signed_out";

export interface ApiClientOptions {
  baseUrl: string;
  tokenStore: TokenStore;
  device: DeviceInfo;
  fetch?: typeof fetch;
  /** Called after every successful sign-in or refresh. */
  onSession?: (session: Session) => void;
  /** Called once when the session can no longer be refreshed. */
  onSignedOut?: (reason: "refresh_rejected" | "logout" | "revoked") => void;
  /** Refresh this many ms before the access token expires. Default 60 s. */
  refreshSkewMs?: number;
  now?: () => number;
  /**
   * Resolves a URL that answers with a redirect and returns the Location. The desktop shell
   * provides a native version (no CORS). Falls back to fetch when absent.
   */
  resolveRedirect?: (url: string, accessToken: string) => Promise<string>;
  /**
   * How the long-lived session is kept. `refresh_token` (default, desktop): a rotating refresh
   * token in `tokenStore`. `cookie` (App mode in a browser): the server's HTTP-only `vd_session`
   * cookie identifies the session, refresh posts no body and `tokenStore` is not used.
   */
  session?: "refresh_token" | "cookie";
  /** The client kind sent at sign-in. Default: `web` with a cookie session, else `desktop`. */
  client?: "desktop" | "console" | "web";
  /**
   * How recording links are read. `redirect` (default): follow the 302. `json`: ask for
   * `?format=json`, for a browser that cannot read a cross-origin redirect.
   */
  recordingLinks?: "redirect" | "json";
  /**
   * Checked before every request: a path the member's seat does not include (ADR 0033) is
   * refused here with `403 not_in_plan` and never sent. Returns the missing entitlement, or null.
   * The method lets a read and a write of one path differ (list and contact reads).
   */
  notInPlan?: (path: string, method: string) => string | null;
}

interface RequestOptions {
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
  /** Let the request outlive the page (sent while the window closes). */
  keepalive?: boolean;
  /** Internal: set on the retry after a refresh so a second 401 does not loop. */
  retried?: boolean;
  /** Send and accept cookies (cookie sessions, auth routes only). */
  credentials?: boolean;
  /** Skip the seat check here: a 911 or 933 call is never refused by the app (NP9). */
  anyPlan?: boolean;
}

/**
 * The calls a typed API (`CampaignsApi`, `BriefsApi`) makes: the desktop's `ApiClient`, or
 * another app's adapter over its own session (the console signs in with a cookie).
 */
export type ApiTransport = Pick<
  ApiClient,
  "get" | "post" | "put" | "patch" | "del" | "streamEvents"
>;

export class ApiClient {
  readonly baseUrl: string;
  private readonly store: TokenStore;
  private device: DeviceInfo;
  private readonly fetchImpl: typeof fetch;
  private readonly opts: ApiClientOptions;
  private accessToken: string | null = null;
  private accessExpiresAt = 0;
  private refreshing: Promise<Session> | null = null;
  private signedOut = false;

  constructor(opts: ApiClientOptions) {
    this.opts = opts;
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.store = opts.tokenStore;
    this.device = opts.device;
    // Bind so a bare global fetch is not called with the client as `this`.
    this.fetchImpl = opts.fetch ?? ((...args) => globalThis.fetch(...args));
  }

  /** Update the device info sent at sign-in (known only after the shell answers). */
  setDevice(device: DeviceInfo) {
    this.device = device;
  }

  private now() {
    return this.opts.now ? this.opts.now() : Date.now();
  }

  private get cookieSession() {
    return this.opts.session === "cookie";
  }

  /** The client kind sent at sign-in. */
  private get clientKind(): "desktop" | "console" | "web" {
    return this.opts.client ?? (this.cookieSession ? "web" : "desktop");
  }

  // ---------- session handling ----------

  /** Has a usable access token in memory. */
  get hasAccessToken() {
    return this.accessToken !== null;
  }

  /** Install a session returned by any sign-in or refresh call. */
  async acceptSession(session: Session): Promise<Session> {
    this.accessToken = session.access_token;
    this.accessExpiresAt = Date.parse(session.access_expires_at) || this.now() + 9 * 60_000;
    this.signedOut = false;
    if (session.refresh_token) await this.store.setRefreshToken(session.refresh_token);
    this.opts.onSession?.(session);
    return session;
  }

  /**
   * Try to restore a session from the stored refresh token. Returns null when there is no
   * token or the server rejects it. Throws ApiError(status 0) when offline so the caller can
   * keep the user signed in and retry.
   */
  async restore(): Promise<Session | null> {
    // A cookie session may exist with nothing stored here: ask the server.
    const token = this.cookieSession ? "cookie" : await this.store.getRefreshToken();
    if (!token) return null;
    try {
      return await this.refresh();
    } catch (e) {
      if (e instanceof ApiError && !e.isNetwork) return null;
      throw e;
    }
  }

  /**
   * Single-flight refresh: concurrent callers share one request, because the server rotates
   * the refresh token and reusing an old one revokes the whole session family.
   */
  refresh(): Promise<Session> {
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  private async doRefresh(): Promise<Session> {
    const token = this.cookieSession ? null : await this.store.getRefreshToken();
    if (!token && !this.cookieSession) {
      this.handleSignedOut("refresh_rejected");
      throw new ApiError(401, "no_refresh_token", "You are signed out.");
    }
    try {
      const session = await this.raw<Session>("POST", "/v1/auth/refresh", {
        // A cookie session sends no body: the cookie identifies the session. The device lets an
        // app updated in place give its session its install id (ADR 0031).
        body: token ? { refresh_token: token, device: this.device } : undefined,
        auth: false,
        credentials: this.cookieSession,
      });
      return await this.acceptSession(session);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 400 || e.status === 403)) {
        await this.store.clear();
        this.handleSignedOut("refresh_rejected");
      }
      throw e;
    }
  }

  private handleSignedOut(reason: "refresh_rejected" | "logout" | "revoked") {
    this.accessToken = null;
    this.accessExpiresAt = 0;
    if (this.signedOut) return;
    this.signedOut = true;
    this.opts.onSignedOut?.(reason);
  }

  /** The server revoked this session (WebSocket `session.revoked`). */
  async revoked() {
    await this.store.clear();
    this.handleSignedOut("revoked");
  }

  /** Current access token, refreshed first if it is about to expire. */
  async getAccessToken(): Promise<string> {
    const skew = this.opts.refreshSkewMs ?? 60_000;
    if (!this.accessToken || this.now() >= this.accessExpiresAt - skew) {
      await this.refresh();
    }
    if (!this.accessToken) throw new ApiError(401, "signed_out", "You are signed out.");
    return this.accessToken;
  }

  // ---------- transport ----------

  private async raw<T>(method: string, path: string, o: RequestOptions = {}): Promise<T> {
    const res = await this.request(method, path, o);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** Sends a request with auth and one refresh retry. Resolves only with a 2xx response. */
  private async request(
    method: string,
    path: string,
    o: RequestOptions = {},
    accept = "application/json",
  ): Promise<Response> {
    // An empty id would send `/v1/calls//wrapup`: refuse it here, typed, so the screen can say
    // what is missing instead of showing the server's 400.
    const missing = emptySegment(path);
    if (missing !== null) throw new EmptyIdError(missing);
    const lacks = o.anyPlan ? null : (this.opts.notInPlan?.(path, method) ?? null);
    if (lacks !== null) {
      throw new ApiError(403, "not_in_plan", "That is not in your plan.", undefined, {
        entitlement: lacks,
      });
    }
    const headers: Record<string, string> = { accept };
    const isForm = typeof FormData !== "undefined" && o.body instanceof FormData;
    const isBlob = typeof Blob !== "undefined" && o.body instanceof Blob;
    // FormData sets its own multipart content type with the boundary; a Blob is sent as is.
    if (isBlob) headers["content-type"] = (o.body as Blob).type || "application/octet-stream";
    else if (o.body !== undefined && !isForm) headers["content-type"] = "application/json";
    if (o.auth !== false) headers.authorization = `Bearer ${await this.getAccessToken()}`;

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body:
          o.body === undefined
            ? undefined
            : isForm || isBlob
              ? (o.body as FormData | Blob)
              : JSON.stringify(o.body),
        signal: o.signal,
        ...(o.keepalive ? { keepalive: true } : {}),
        ...(o.credentials ? { credentials: "include" as const } : {}),
      });
    } catch (e) {
      if (isAbortError(e)) throw e;
      throw new ApiError(0, "network", "Can't reach the Kite server.");
    }

    if (res.status === 401 && o.auth !== false && !o.retried) {
      // The access token was rejected early (clock skew, server restart with new keys).
      this.accessToken = null;
      await this.refresh();
      return this.request(method, path, { ...o, retried: true }, accept);
    }

    this.noteServerClock(res);
    if (!res.ok) throw await toApiError(res);
    return res;
  }

  /** Server time minus local time, from the last response's `Date` header (0 until known). */
  private serverSkewMs = 0;

  private noteServerClock(res: Response) {
    const date = res.headers?.get?.("date");
    const at = date ? Date.parse(date) : Number.NaN;
    // `Date` has whole seconds: the server's clock is somewhere in that second.
    if (Number.isFinite(at)) this.serverSkewMs = at + 500 - Date.now();
  }

  /** The server's clock now, as well as the app can tell (for deadlines the server sets). */
  serverNow(): number {
    return Date.now() + this.serverSkewMs;
  }

  get<T>(path: string, signal?: AbortSignal) {
    return this.raw<T>("GET", path, { signal });
  }
  post<T>(path: string, body?: unknown) {
    return this.raw<T>("POST", path, { body });
  }
  put<T>(path: string, body?: unknown) {
    return this.raw<T>("PUT", path, { body });
  }
  patch<T>(path: string, body?: unknown) {
    return this.raw<T>("PATCH", path, { body });
  }
  del<T>(path: string) {
    return this.raw<T>("DELETE", path);
  }

  // ---------- auth ----------

  /** `graphToken`: a Microsoft Graph token from the same sign-in, for the member's photo. */
  async signInWithMicrosoft(idToken: string, graphToken?: string | null): Promise<Session> {
    const session = await this.raw<Session>("POST", "/v1/auth/microsoft", {
      auth: false,
      body: {
        id_token: idToken,
        client: this.clientKind,
        device: this.device,
        ...(graphToken ? { graph_token: graphToken } : {}),
      },
      credentials: this.cookieSession,
    });
    return this.acceptSession(session);
  }

  /** Email-first routing: what to offer for an email's domain (ADR 0032). No session needed. */
  signInRoute(email: string): Promise<SignInRoute> {
    return this.raw<SignInRoute>("POST", "/v1/auth/route", { auth: false, body: { email } });
  }

  /** Finishes a browser sign-in: trades the handoff ticket and its PKCE verifier for a session. */
  async signInWithTicket(ticket: string, verifier: string): Promise<Session> {
    const session = await this.raw<Session>("POST", "/v1/auth/ticket", {
      auth: false,
      body: { ticket, verifier, device: this.device },
    });
    return this.acceptSession(session);
  }

  /** Development servers only. */
  async signInDev(email: string): Promise<Session> {
    const body: DevSignInRequest = { email, client: this.clientKind, device: this.device };
    const session = await this.raw<Session>("POST", "/v1/auth/dev", {
      auth: false,
      body,
      credentials: this.cookieSession,
    });
    return this.acceptSession(session);
  }

  async logout(): Promise<void> {
    try {
      if (this.accessToken)
        await this.raw<void>("POST", "/v1/auth/logout", { credentials: this.cookieSession });
    } catch {
      // Signing out locally must work offline too.
    }
    await this.store.clear();
    this.handleSignedOut("logout");
  }

  me(signal?: AbortSignal) {
    return this.get<Me>("/v1/me", signal);
  }
  updateSettings(settings: UserSettings) {
    return this.put<Me>("/v1/me/settings", settings);
  }
  /**
   * One phone per person (ADR 0035): this client's phone is ready, so it takes the phone. Every
   * other client of the member hears `phone.claimed` and lets go.
   */
  claimPhone(clientId: string) {
    return this.post<void>("/v1/phone/claim", { client_id: clientId });
  }

  // ---------- telephony and calls ----------

  telephonyToken() {
    return this.get<TelephonyToken>("/v1/telephony/token");
  }
  lookup(phone: string, signal?: AbortSignal) {
    return this.get<ContextCard>(`/v1/lookup?phone=${encodeURIComponent(phone)}`, signal);
  }
  startCall(req: StartCallRequest) {
    // 911 and 933 go to the server whatever the seat; the server places them (NP8).
    if (emergencyNumber(req.to)) {
      return this.raw<StartCallResponse>("POST", "/v1/calls", { body: req, anyPlan: true });
    }
    return this.post<StartCallResponse>("/v1/calls", req);
  }
  /** The rep's own calls, newest first; `q`, `direction` and `show` search and filter. */
  listCalls(params: CallListParams = {}, signal?: AbortSignal) {
    return this.get<Page<CallSummaryRow>>(`/v1/calls${callListQuery(params)}`, signal);
  }
  /** A member's calls, for someone who manages them (404 for anyone else). */
  memberCalls(userId: string, params: CallListParams = {}, signal?: AbortSignal) {
    return this.get<Page<CallSummaryRow>>(
      `/v1/team/${encodeURIComponent(userId)}/calls${callListQuery(params)}`,
      signal,
    );
  }
  /** Contacts by name, company or phone: ours first, then the rep's CRM. */
  searchContacts(q: string, signal?: AbortSignal) {
    return this.get<ContactList>(`/v1/contacts?q=${encodeURIComponent(q)}`, signal);
  }
  getCall(id: string, signal?: AbortSignal) {
    return this.get<CallDetail>(`/v1/calls/${encodeURIComponent(id)}`, signal);
  }
  /** The member's unseen missed calls and unheard voicemails (Phone badge, Missed and
   * Voicemail tabs). Opening a call's detail marks it seen too. */
  unseenCalls(signal?: AbortSignal) {
    return this.get<UnseenCounts>("/v1/calls/unseen", signal);
  }
  /** Marks one missed call seen. Repeating it changes nothing. */
  markCallSeen(id: string) {
    return this.post<UnseenCounts>(`/v1/calls/${encodeURIComponent(id)}/seen`);
  }
  /** Mark all as seen: every missed call that started before `before` (now when left out). */
  markAllCallsSeen(before?: string) {
    const body: MarkSeenRequest = before ? { before } : {};
    return this.post<UnseenCounts>("/v1/calls/seen", body);
  }
  /** Review later: save the call's wrap-up for review (the list item moves now). */
  saveDraft(id: string, body: Wrapup, opts: { keepalive?: boolean } = {}) {
    return this.raw<WrapupDraft>("PUT", `/v1/calls/${id}/draft`, {
      body: withActivity(body),
      ...(opts.keepalive ? { keepalive: true } : {}),
    });
  }
  wrapupDrafts(signal?: AbortSignal) {
    return this.get<WrapupDraftList>("/v1/wrapup-drafts", signal);
  }
  discardDraft(id: string) {
    return this.del<void>(`/v1/calls/${id}/draft`);
  }
  /** The dispositions to pick from, in order with their keys (ADR 0015). */
  dispositions(q: DispositionsQuery = {}, signal?: AbortSignal) {
    const p = new URLSearchParams();
    if (q.campaign_id) p.set("campaign_id", q.campaign_id);
    if (q.list_id) p.set("list_id", q.list_id);
    if (q.include_archived) p.set("include_archived", "true");
    const qs = p.toString();
    return this.get<DispositionList>(`/v1/dispositions${qs ? `?${qs}` : ""}`, signal);
  }
  /** Who decides which next steps become CRM activities: the company and campaign settings. */
  nextStepActivities(signal?: AbortSignal) {
    return this.get<NextStepActivitiesSettings>(
      "/v1/admin/dispositions/next-step-activities",
      signal,
    );
  }
  setNextStepActivities(value: NextStepActivities) {
    return this.put<NextStepActivitiesSettings>("/v1/admin/dispositions/next-step-activities", {
      value,
    });
  }
  /** A campaign's own setting, or null to inherit again. */
  setCampaignNextStepActivities(campaignId: string, value: NextStepActivities | null) {
    return this.put<NextStepActivitiesSettings>(
      `/v1/admin/campaigns/${campaignId}/next-step-activities`,
      { value },
    );
  }
  // ---------- Parallel Dial ----------

  pdialAvailability(listId: string | null, signal?: AbortSignal) {
    const q = listId ? `?list_id=${encodeURIComponent(listId)}` : "";
    return this.get<PdialAvailabilityView>(`/v1/pdial/availability${q}`, signal);
  }
  /** The rep's open parallel session (404 without one). */
  pdialCurrentSession(signal?: AbortSignal) {
    return this.get<PdialSession>("/v1/pdial/sessions/current", signal);
  }
  patchPdialSession(id: string, body: PdialSessionPatch) {
    return this.patch<PdialSession>(`/v1/pdial/sessions/${encodeURIComponent(id)}`, body);
  }
  /** Start a parallel session for a Focus scope (null for Today). 409 with the refusal code. */
  startPdialSession(body: { list_id?: string | null; lines?: number }) {
    return this.post<PdialStarted>("/v1/pdial/sessions", body);
  }
  stopPdialSession(id: string) {
    return this.del<PdialSession>(`/v1/pdial/sessions/${encodeURIComponent(id)}`);
  }
  /** Skip a voicemail (hang up and dial on). */
  pdialVoicemail(id: string, choice: "skip" | "drop") {
    return this.post<PdialSession>(`/v1/pdial/sessions/${encodeURIComponent(id)}/voicemail`, {
      choice,
    });
  }
  /** The next batch now (after a wrap-up). */
  pdialNext(id: string) {
    return this.post<PdialSession>(`/v1/pdial/sessions/${encodeURIComponent(id)}/next`);
  }
  /**
   * The rep is there, from the app holding the session's audio leg (`legKey`, the key it put
   * in that leg's client state). 204; 409 `not_leg_holder` from any other app.
   */
  pdialPresence(id: string, legKey: string) {
    return this.post<void>(`/v1/pdial/sessions/${encodeURIComponent(id)}/presence`, {
      leg_key: legKey,
    });
  }
  /** End call on the winner: the person's leg hangs up, the rep's audio stays. */
  pdialHangup(id: string) {
    return this.post<PdialSession>(`/v1/pdial/sessions/${encodeURIComponent(id)}/hangup`);
  }
  wrapup(id: string, body: Wrapup) {
    return this.post<CallDetail>(`/v1/calls/${encodeURIComponent(id)}/wrapup`, withActivity(body));
  }
  moveCall(id: string, destination: Destination) {
    return this.post<CallDetail>(`/v1/calls/${encodeURIComponent(id)}/move`, { destination });
  }
  /** Changes a wrapped-up call's disposition (the contact record's Last call). */
  changeDisposition(id: string, body: ChangeDisposition) {
    return this.post<DispositionChanged>(
      `/v1/calls/${encodeURIComponent(id)}/disposition`,
      withActivity(body),
    );
  }

  // ---------- transfer and Add person ----------

  transferCall(id: string, req: TransferRequest) {
    return this.post<CallParticipants>(`/v1/calls/${encodeURIComponent(id)}/transfer`, req);
  }
  completeTransfer(id: string) {
    return this.post<CallParticipants>(`/v1/calls/${encodeURIComponent(id)}/transfer/complete`);
  }
  cancelTransfer(id: string) {
    return this.post<CallParticipants>(`/v1/calls/${encodeURIComponent(id)}/transfer/cancel`);
  }
  addParticipant(id: string, to: CallTarget) {
    return this.post<Participant>(`/v1/calls/${encodeURIComponent(id)}/participants`, { to });
  }
  /** Move here, Join, or the pushed Answer: a token for this device's pickup leg. */
  pickupCall(id: string, mode: PickupMode) {
    return this.post<PickupResponse>(`/v1/calls/${encodeURIComponent(id)}/pickup`, { mode });
  }
  removeParticipant(id: string, participantId: string) {
    return this.del<CallParticipants>(
      `/v1/calls/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}`,
    );
  }

  /** Pauses (`paused`) or resumes the live call's recording. Repeating it changes nothing. */
  setRecordingPaused(id: string, paused: boolean) {
    return this.post<RecordingState>(
      `/v1/calls/${encodeURIComponent(id)}/recording/${paused ? "pause" : "resume"}`,
    );
  }

  /**
   * The recording endpoint answers 302 to a presigned URL valid for 5 minutes. Returns that
   * URL so an <audio> element can stream it without our bearer token.
   */
  async recordingUrl(id: string): Promise<string> {
    if (this.opts.recordingLinks === "json") {
      const r = await this.get<RecordingUrl>(
        `/v1/calls/${encodeURIComponent(id)}/recording?format=json`,
      );
      return r.url;
    }
    const url = `${this.baseUrl}/v1/calls/${encodeURIComponent(id)}/recording`;
    const token = await this.getAccessToken();
    if (this.opts.resolveRedirect) return this.opts.resolveRedirect(url, token);
    let res: Response;
    const manual: WebRequestInit = {
      method: "GET",
      headers: { authorization: `Bearer ${token}` },
      redirect: "manual",
    };
    try {
      res = await this.fetchImpl(url, manual);
    } catch {
      throw new ApiError(0, "network", "Can't reach the Kite server.");
    }
    const location = res.headers.get("location");
    if (location) return new URL(location, url).toString();
    if (res.type === "opaqueredirect" || (res.ok && res.url && res.url !== url)) {
      // Browsers hide Location on manual redirects; follow it and read the final URL.
      const follow: WebRequestInit = {
        headers: { authorization: `Bearer ${token}` },
        redirect: "follow",
      };
      const followed = await this.fetchImpl(url, follow);
      cancelBody(followed);
      if (followed.url) return followed.url;
    }
    if (!res.ok && res.type !== "opaqueredirect") throw await toApiError(res);
    throw new ApiError(res.status, "no_redirect", "The recording link could not be opened.");
  }

  // ---------- members' photos (ADR 0029) ----------

  /** Every member of the tenant who has a photo, with its version. */
  photos(signal?: AbortSignal) {
    return this.get<PhotoList>("/v1/photos", signal);
  }

  /** One member's photo (a 96px JPEG). The version is in the address, so it caches for good. */
  async photo(userId: string, version: string, signal?: AbortSignal): Promise<Blob> {
    const res = await this.request(
      "GET",
      `/v1/photos/${encodeURIComponent(userId)}/${encodeURIComponent(version)}`,
      { signal },
      "image/jpeg",
    );
    return res.blob();
  }

  // ---------- workday: contacts, lists, today ----------

  /** Server-paged CRM contacts table. */
  browseContacts(params: ContactBrowseParams = {}, signal?: AbortSignal) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) {
        if (v.length) q.set(k, v.join(","));
      } else q.set(k, String(v));
    }
    const qs = q.toString();
    return this.get<ContactPage>(`/v1/crm/contacts${qs ? `?${qs}` : ""}`, signal);
  }
  crmTags(signal?: AbortSignal) {
    return this.get<TagList>("/v1/crm/tags", signal);
  }
  /** Where you left off. */
  contactContext(ref: CrmRef, signal?: AbortSignal) {
    return this.get<ContactContext>(
      `/v1/crm/contacts/${encodeURIComponent(refPath(ref))}/context`,
      signal,
    );
  }
  /** Adds or fixes a phone or email; written to the CRM under the rep's own user. */
  updateContact(ref: CrmRef, body: ContactUpdate) {
    return this.patch<ContactRow>(
      `/v1/crm/contacts/${encodeURIComponent(ref.model)}/${encodeURIComponent(String(ref.id))}`,
      body,
    );
  }
  /** Every open activity on the contact and its open opportunities, soonest first. */
  contactActivities(ref: CrmRef, signal?: AbortSignal) {
    return this.get<ActivityList>(
      `/v1/crm/contacts/${encodeURIComponent(ref.model)}/${encodeURIComponent(String(ref.id))}/activities`,
      signal,
    );
  }
  /** The tenant's activity types (cached on the server for 10 minutes). */
  activityTypes(signal?: AbortSignal) {
    return this.get<ActivityTypeList>("/v1/crm/activity-types", signal);
  }
  /** Schedules an activity for the rep, written with the rep's own CRM key. */
  scheduleActivity(body: NewActivity) {
    return this.post<Activity>("/v1/crm/activities", body);
  }
  /** Marks an activity done, with an optional feedback note. */
  completeActivity(id: string | number, feedback?: string) {
    return this.post<ActivityChanged>(
      `/v1/crm/activities/${encodeURIComponent(String(id))}/done`,
      feedback ? { feedback } : {},
    );
  }
  /** Marks an Odoo opportunity won with the rep's own key, through the outbox (202, pending). */
  markWon(crmId: string | number) {
    return this.post<MarkWonStatus>(`/v1/opportunities/${encodeURIComponent(String(crmId))}/won`);
  }
  /** Where marking an opportunity won stands. */
  markWonStatus(crmId: string | number, signal?: AbortSignal) {
    return this.get<MarkWonStatus>(
      `/v1/opportunities/${encodeURIComponent(String(crmId))}/won`,
      signal,
    );
  }
  /** Moves an activity to a new due date (YYYY-MM-DD). */
  rescheduleActivity(id: string | number, due: string) {
    return this.patch<ActivityChanged>(`/v1/crm/activities/${encodeURIComponent(String(id))}`, {
      due,
    });
  }
  /** Contact mirror sync status. */
  crmSyncStatus(signal?: AbortSignal) {
    return this.get<CrmSyncStatus>("/v1/crm/sync", signal);
  }
  /** Starts a mirror sync (debounced); progress arrives as crm.sync events. */
  startCrmSync(body: { full?: boolean } = {}) {
    return this.post<CrmSyncStarted>("/v1/crm/sync", body);
  }
  /** Every ref matching a filter (capped at 2,000), for "Select all N matching". */
  resolveContacts(body: ResolveRequest) {
    return this.post<ResolveResponse>("/v1/crm/contacts/resolve", body);
  }

  /** Own lists; `team` (managers) every list of their reps; `user_id` one rep's lists. */
  listLists(
    params: { archived?: boolean; team?: boolean; user_id?: string } = {},
    signal?: AbortSignal,
  ) {
    const q = new URLSearchParams();
    if (params.archived) q.set("archived", "true");
    if (params.team) q.set("team", "true");
    if (params.user_id) q.set("user_id", params.user_id);
    const qs = q.toString();
    return this.get<CallListList>(`/v1/lists${qs ? `?${qs}` : ""}`, signal);
  }
  createList(body: NewList) {
    return this.post<ListDetail>("/v1/lists", body);
  }
  getList(
    id: string,
    params: { status?: ItemStatus; outcomes?: ItemOutcome[] } = {},
    signal?: AbortSignal,
  ) {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.outcomes?.length) q.set("outcomes", params.outcomes.join(","));
    const qs = q.toString();
    return this.get<ListDetail>(`/v1/lists/${encodeURIComponent(id)}${qs ? `?${qs}` : ""}`, signal);
  }
  updateList(id: string, patch: ListPatch) {
    return this.patch<CallList>(`/v1/lists/${encodeURIComponent(id)}`, patch);
  }
  /** Archives the list (restore with updateList(id, {archived: false})). */
  deleteList(id: string) {
    return this.del<void>(`/v1/lists/${encodeURIComponent(id)}`);
  }
  addListItems(id: string, body: AddItems) {
    return this.post<ItemsAdded>(`/v1/lists/${encodeURIComponent(id)}/items`, body);
  }
  /** Returns the updated item, or null when it was removed (204). */
  async updateListItem(listId: string, itemId: string, patch: ItemPatch): Promise<ListItem | null> {
    const r = await this.patch<ListItem | undefined>(
      `/v1/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
      patch,
    );
    return r ?? null;
  }
  bulkListItems(id: string, body: BulkItems) {
    return this.post<BulkResult>(`/v1/lists/${encodeURIComponent(id)}/items/bulk`, body);
  }
  /** Mark done, reschedule or remove items picked on Today, across lists. */
  todayBulk(body: TodayBulk) {
    return this.post<TodayBulkResult>("/v1/today/items/bulk", body);
  }
  /** Undo a bulk remove, a move or a sort with its undo_token (valid 10 minutes). */
  restoreListItems(id: string, undoToken: string) {
    return this.post<RestoreResult>(`/v1/lists/${encodeURIComponent(id)}/items/restore`, {
      undo_token: undoToken,
    });
  }
  sortList(id: string, body: ListSort) {
    return this.post<SortResult>(`/v1/lists/${encodeURIComponent(id)}/sort`, body);
  }
  refreshList(id: string) {
    return this.post<RefreshResult>(`/v1/lists/${encodeURIComponent(id)}/refresh`);
  }

  today(params: { tz?: string; refresh?: boolean } = {}, signal?: AbortSignal) {
    const q = new URLSearchParams();
    if (params.tz) q.set("tz", params.tz);
    if (params.refresh) q.set("refresh", "true");
    const qs = q.toString();
    return this.get<Today>(`/v1/today${qs ? `?${qs}` : ""}`, signal);
  }
  /** Claims the next item (marks it in_progress) and returns it with its context. */
  nextItem(body: NextRequest = {}) {
    return this.post<NextItem>("/v1/today/next", body);
  }
  /** The Focus queue rail: Up next in serving order and the segment counts. */
  upcoming(params: { list_id?: string; limit?: number; tz?: string } = {}, signal?: AbortSignal) {
    const q = new URLSearchParams();
    if (params.list_id) q.set("list_id", params.list_id);
    if (params.limit !== undefined) q.set("limit", String(params.limit));
    if (params.tz) q.set("tz", params.tz);
    const qs = q.toString();
    return this.get<Upcoming>(`/v1/today/upcoming${qs ? `?${qs}` : ""}`, signal);
  }
  /** Every item of a list or the Today queue in rail order (vertical rail, browsing). */
  queueItems(params: { list_id?: string; tz?: string } = {}, signal?: AbortSignal) {
    const q = new URLSearchParams();
    if (params.list_id) q.set("list_id", params.list_id);
    if (params.tz) q.set("tz", params.tz);
    const qs = q.toString();
    return this.get<QueueItems>(`/v1/today/queue${qs ? `?${qs}` : ""}`, signal);
  }
  /** What a calling session got done and what remains, for a list or (no list) Today. */
  sessionSummary(
    params: { list_id?: string | null; since?: string; tz?: string } = {},
    signal?: AbortSignal,
  ) {
    const q = new URLSearchParams();
    if (params.since) q.set("since", params.since);
    if (params.tz) q.set("tz", params.tz);
    const qs = q.toString();
    const path = params.list_id
      ? `/v1/lists/${encodeURIComponent(params.list_id)}/session-summary`
      : "/v1/today/session-summary";
    return this.get<SessionSummary>(`${path}${qs ? `?${qs}` : ""}`, signal);
  }
  /** The rep's active calling session, if any. */
  activeSession(signal?: AbortSignal) {
    return this.get<ActiveSession>("/v1/sessions/active", signal);
  }
  /**
   * Starts a calling session, or resumes the active or paused one of that scope. Another active
   * scope answers 409 session_active unless pause_active is set.
   */
  startSession(body: { list_id?: string | null; tz?: string; pause_active?: boolean } = {}) {
    return this.post<FocusSession>("/v1/sessions", body);
  }
  /** Pauses a session (its claim goes back to the list; current_item_id is kept). */
  pauseSession(id: string) {
    return this.post<void>(`/v1/sessions/${encodeURIComponent(id)}/pause`);
  }
  endSession(id: string, reason: "ended" | "completed" = "ended") {
    return this.post<void>(`/v1/sessions/${encodeURIComponent(id)}/end`, { reason });
  }
  /** On-demand CRM sync, debounced server-side to once a minute per rep. */
  syncNow() {
    return this.post<SyncResult>("/v1/sync");
  }

  // ---------- team ----------

  /** Everyone in the tenant with their number and presence, sorted by name. */
  team(signal?: AbortSignal) {
    return this.get<TeamList>("/v1/team", signal);
  }

  // ---------- team tools (ADR 0007): Live now, monitoring, coaching notes ----------

  /** Live calls of the reps the signed-in manager manages. */
  teamLive(signal?: AbortSignal) {
    return this.get<LiveCallList>("/v1/team/live", signal);
  }
  /**
   * Listen, whisper or join a team call; the app is rung and answers by itself. `notifyRep` is
   * the manager's answer to "Let Jared know you're listening?" (left out: the rep's policy).
   */
  startMonitor(callId: string, mode: MonitorMode, notifyRep?: boolean) {
    return this.post<CallMonitorEvent>(
      `/v1/calls/${encodeURIComponent(callId)}/monitor`,
      notifyRep === undefined ? { mode } : { mode, notify_rep: notifyRep },
    );
  }
  switchMonitor(callId: string, mode: MonitorMode) {
    return this.patch<CallMonitorEvent>(`/v1/calls/${encodeURIComponent(callId)}/monitor`, {
      mode,
    });
  }
  stopMonitor(callId: string) {
    return this.del<CallMonitorEvent>(`/v1/calls/${encodeURIComponent(callId)}/monitor`);
  }
  coachingNotes(callId: string) {
    return this.get<CoachingNoteList>(`/v1/calls/${encodeURIComponent(callId)}/notes`);
  }
  addCoachingNote(callId: string, body: string) {
    return this.post<CoachingNote>(`/v1/calls/${encodeURIComponent(callId)}/notes`, { body });
  }
  deleteCoachingNote(callId: string, noteId: string) {
    return this.del<void>(
      `/v1/calls/${encodeURIComponent(callId)}/notes/${encodeURIComponent(noteId)}`,
    );
  }

  // ---------- integrations ----------

  crmStatus() {
    return this.get<CrmStatus>("/v1/integrations/crm");
  }
  connectOdoo(body: ConnectOdooRequest) {
    return this.post<CrmStatus>("/v1/integrations/crm/odoo", body);
  }
  disconnectCrm() {
    return this.del<CrmStatus>("/v1/integrations/crm");
  }

  /** The Kite connector: whether the rep may connect, and their live connections. */
  myConnector() {
    return this.get<MyConnector>("/v1/me/connector");
  }
  /** Ends one of the rep's own connections at once (204). */
  disconnectConnector(id: string) {
    return this.del<void>(`/v1/me/connector/grants/${encodeURIComponent(id)}`);
  }

  // ---------- telemetry ----------

  sendEvents(events: ClientEvent[]) {
    return this.post<void>("/v1/telemetry/events", { events });
  }
  sendCallQuality(body: CallQualityUpload) {
    return this.post<void>("/v1/telemetry/call-quality", body);
  }

  // ---------- problem reports ----------

  /** Multipart upload. Safe to retry with the same `id`. */
  createBugReport(r: BugReportUpload) {
    const form = new FormData();
    form.append("id", r.id);
    if (r.note) form.append("note", r.note);
    if (r.call_id) form.append("call_id", r.call_id);
    form.append("diagnostics", JSON.stringify(r.diagnostics));
    // The audio goes last so the server reads the small fields first.
    if (r.audio) appendFile(form, "audio", r.audio, r.audio_name ?? "report.webm");
    return this.post<BugReportAccepted>("/v1/bug-reports", form);
  }

  // ---------- support assistant ----------

  /** The rep's past conversations with the assistant, newest first. */
  listAssistantSessions() {
    return this.get<{ items: ConversationSummary[] }>("/v1/assistant/sessions");
  }
  getAssistantSession(sessionId: string) {
    return this.get<AssistantSession>(`/v1/assistant/sessions/${sessionId}`);
  }

  startAssistant(body: StartAssistant) {
    return this.post<AssistantSession>("/v1/assistant/sessions", body);
  }

  /**
   * Sends a typed message and yields the reply as it streams (server-sent events). Throws
   * ApiError before the first event (404, 409, 422, 503); a failure mid-reply arrives as an
   * `error` event.
   */
  async *assistantMessage(
    sessionId: string,
    text: string,
    signal?: AbortSignal,
    context?: unknown,
    attachmentIds?: string[],
  ): AsyncGenerator<ChatStreamEvent> {
    const body: Record<string, unknown> = { text };
    if (context) body.context = context;
    if (attachmentIds?.length) body.attachment_ids = attachmentIds;
    yield* this.streamEvents<ChatStreamEvent>(
      `/v1/assistant/sessions/${sessionId}/messages`,
      body,
      signal,
    );
  }

  /**
   * Records the option the person clicked under an Assistant reply (`ask_choice`). Once: the
   * same option again returns the choice; another is refused (409 `already_chosen`).
   */
  chooseOption(sessionId: string, messageId: string, index: number) {
    return this.post<ChoicePrompt>(`/v1/assistant/sessions/${sessionId}/choices/${messageId}`, {
      index,
    });
  }

  /**
   * The Assistant's opening turn in a conversation the app opened with a purpose (New
   * research): it speaks first. Nothing is sent as the rep's words.
   */
  async *assistantOpening(
    sessionId: string,
    signal?: AbortSignal,
  ): AsyncGenerator<ChatStreamEvent> {
    yield* this.streamEvents<ChatStreamEvent>(
      `/v1/assistant/sessions/${sessionId}/opening`,
      {},
      signal,
    );
  }

  /**
   * POSTs `body` and yields the server-sent events of the reply, one JSON object each. Throws
   * ApiError before the first event; a failure mid-stream arrives as the server's own event.
   */
  async *streamEvents<E>(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<E> {
    const res = await this.request("POST", path, { body, signal }, "text/event-stream");
    const parse = parseSse as unknown as (raw: string) => Generator<E>;
    const reader = bodyReader(res);
    if (!reader) {
      yield* parse(await res.text());
      return;
    }
    const decoder = utf8Decoder();
    let buf = "";
    try {
      while (true) {
        let chunk: { done: boolean; value?: Uint8Array };
        try {
          chunk = await reader.read();
        } catch (e) {
          if (isAbortError(e)) throw e;
          throw new ApiError(0, "network", "The connection to the server dropped.");
        }
        if (chunk.done) break;
        buf += decoder.decode(chunk.value, { stream: true }).replace(/\r\n/g, "\n");
        let cut = buf.indexOf("\n\n");
        while (cut >= 0) {
          yield* parse(buf.slice(0, cut));
          buf = buf.slice(cut + 2);
          cut = buf.indexOf("\n\n");
        }
      }
      if (buf.trim()) yield* parse(buf);
    } finally {
      reader.releaseLock();
    }
  }

  assistantVoice(sessionId: string) {
    return this.post<VoiceGrant>(`/v1/assistant/sessions/${sessionId}/voice`);
  }
  /** `closed`: the voice connection ended, so the server settles and closes it. */
  assistantTurns(sessionId: string, turns: NewTurn[], voiceSeconds?: number, closed = false) {
    return this.post<void>(`/v1/assistant/sessions/${sessionId}/turns`, {
      turns,
      voice_seconds: voiceSeconds ?? null,
      ...(closed ? { closed: true } : {}),
    });
  }
  assistantTool(sessionId: string, call: ToolInvocation) {
    return this.post<ToolResult>(`/v1/assistant/sessions/${sessionId}/tools`, call);
  }
  assistantDraft(sessionId: string) {
    return this.post<ReportDraft>(`/v1/assistant/sessions/${sessionId}/draft`);
  }
  /** The rep's edit to the live report: the fields become theirs. */
  assistantEditReport(sessionId: string, patch: import("./report.ts").ReportPatch) {
    return this.patch<import("./report.ts").LiveReport>(
      `/v1/assistant/sessions/${sessionId}/report`,
      patch,
    );
  }
  assistantSubmit(sessionId: string) {
    return this.post<{ report_id: string }>(`/v1/assistant/sessions/${sessionId}/submit`);
  }
  assistantEnd(sessionId: string, reason: "closed" | "fallback", voiceSeconds?: number) {
    return this.post<void>(`/v1/assistant/sessions/${sessionId}/end`, {
      reason,
      voice_seconds: voiceSeconds ?? null,
    });
  }

  // ---------- after the report is filed ----------

  /** Support messages the rep has not seen (the sidebar badge). */
  supportUnread() {
    return this.get<SupportUnread>("/v1/assistant/unread");
  }
  /** Marks a conversation read; answers the unread count left. */
  supportRead(sessionId: string) {
    return this.post<SupportUnread>(`/v1/assistant/sessions/${sessionId}/read`);
  }
  /** Writes to support in a filed report's conversation (409 once it is resolved). */
  supportReply(sessionId: string, text: string) {
    return this.post<AssistantTurn>(`/v1/assistant/sessions/${sessionId}/reply`, { text });
  }
  /** Withdraws the rep's own report (409 once the team resolved it). */
  withdrawReport(sessionId: string, reason?: string) {
    return this.post<ReportProgress>(`/v1/assistant/sessions/${sessionId}/withdraw`, {
      ...(reason?.trim() ? { reason: reason.trim() } : {}),
    });
  }
  /** Reopens a report the rep withdrew (409 past the window). */
  reopenReport(sessionId: string) {
    return this.post<ReportProgress>(`/v1/assistant/sessions/${sessionId}/reopen`);
  }

  /** What's new for recent versions, newest first. Public: no sign-in needed. */
  whatsNew(limit = 3, signal?: AbortSignal) {
    return this.raw<WhatsNewList>("GET", `/v1/whats-new?limit=${Math.trunc(limit)}`, {
      auth: false,
      signal,
    });
  }

  /**
   * WebSocket URL for the realtime channel, with the access token in the query string. The
   * desktop app adds `platform`, `arch`, `channel` and `app_version` so it hears update pushes.
   */
  async websocketUrl(params: Record<string, string | null | undefined> = {}): Promise<string> {
    const token = await this.getAccessToken();
    // Built from parts: React Native's URL cannot change its protocol.
    const u = new URL(`${this.baseUrl}/v1/ws`);
    const q = new URLSearchParams();
    q.set("access_token", token);
    for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
    return `${u.protocol === "https:" ? "wss:" : "ws:"}//${u.host}${u.pathname}?${q.toString()}`;
  }
}

/** Parses one or more SSE events (`data:` lines) into chat stream events. */
export function* parseSse(raw: string): Generator<ChatStreamEvent> {
  for (const block of raw.split(/\n\n/)) {
    const data = block
      .split("\n")
      .filter((l) => l.startsWith("data:"))
      .map((l) => l.slice(5).trimStart())
      .join("\n")
      .trim();
    if (!data) continue;
    try {
      yield JSON.parse(data) as ChatStreamEvent;
    } catch {
      // A malformed event is skipped; the stream continues.
    }
  }
}

/** A CRM ref as written in URL paths: "model:id". */
export function refPath(ref: CrmRef): string {
  return `${ref.model}:${ref.id}`;
}

async function toApiError(res: Response): Promise<ApiError> {
  const requestId = res.headers.get("x-request-id") ?? undefined;
  try {
    const body = (await res.json()) as Partial<ApiErrorBody>;
    if (body?.error?.message) {
      const err = new ApiError(
        res.status,
        body.error.code ?? "error",
        body.error.message,
        body.error.request_id ?? requestId,
        body as Record<string, unknown>,
      );
      const wait = Number(res.headers.get("retry-after"));
      if (Number.isFinite(wait) && wait > 0) err.retryAfter = wait;
      return err;
    }
  } catch {
    // Non-JSON error bodies fall through to a generic message.
  }
  return new ApiError(res.status, `http_${res.status}`, defaultMessage(res.status), requestId);
}

function defaultMessage(status: number): string {
  if (status === 401) return "Your session has expired. Sign in again.";
  if (status === 403) return "You don't have access to that.";
  if (status === 404) return "That item no longer exists.";
  if (status === 429) return "Too many requests. Try again in a moment.";
  if (status >= 500) return "The server had a problem. Try again in a moment.";
  return "The request failed.";
}

/** Query string for `GET /v1/calls` and `GET /v1/team/{id}/calls` ("" when empty). */
export function callListQuery(params: CallListParams): string {
  const q = new URLSearchParams();
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.limit) q.set("limit", String(params.limit));
  const term = params.q?.trim();
  if (term) q.set("q", term);
  if (params.direction) q.set("direction", params.direction);
  if (params.show && params.show !== "all") q.set("show", params.show);
  const qs = q.toString();
  return qs ? `?${qs}` : "";
}
